import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import { toast } from 'react-toastify'
import { actions as appActions } from '../state'
import apis from './FractionApi'
import { actions } from './FractionState'
import { ReturnCall } from '../types'
import { PayloadAction } from '@reduxjs/toolkit'
import {
  FractionTag, GetScaleWeightPayload, PauseFractioningPayload, PrintFractionTagPayload,
} from './FractionTypes'

function* getScaleWeight(data: PayloadAction<GetScaleWeightPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { onSuccess } = data.payload

    const result: ReturnCall<typeof apis.getScaleWeight> = yield call(
      apis.getScaleWeight,
    )

    if (!result.isOk) {
      toast.error('Falha ao recuperar valor pesado na balança!')
      return
    }

    const { weight } = result.data

    yield put(actions.setScaleWeight({ weight: weight ? weight * 1000 : undefined }))

    onSuccess(weight)
  } catch (err: any) {
    toast.error('Falha ao recuperar valor pesado na balança!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* checkLocalRaspberryStatus() {
  try {
    yield put(actions.clearField('isLocalRaspberryConnected'))
    yield put(actions.clearField('printerServer'))

    const result: ReturnCall<typeof apis.checkLocalRaspberryStatus> = yield call(
      apis.checkLocalRaspberryStatus,
    )

    if (!result.isOk && !result.data) {
      return
    }

    if (result.isOk && !result.data?.features?.includes('IMPRESSAO_ETIQUETAS_FRACIONAMENTO')) {
      toast.warn('Alma desatualizada!')
      return
    }

    yield put(actions.setPrinterServer(Boolean(result.data?.printerServer)))
    yield put(actions.setLocalRaspberryConnected(true))
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.error(err)
  }
}

function* checkRemotePrinterStatus() {
  yield put(appActions.isLoading(true))
  try {
    yield put(actions.clearField('isRemotePrinterConnected'))

    const result: ReturnCall<typeof apis.checkRemotePrinterStatus> = yield call(
      apis.checkRemotePrinterStatus,
    )

    if (!result.isOk) {
      if (result.data?.data?.message === 'PRINTER_DISCONNECTED') {
        toast.error('Impressora remota não conectada')
      } else {
        toast.error('Falha ao verificar status da impressora remota')
      }
      return
    }

    if (result.isOk && !result.data?.features?.includes('IMPRESSAO_ETIQUETAS_FRACIONAMENTO')) {
      toast.warn('Alma desatualizada!')
      return
    }

    yield put(actions.setRemotePrinterConnected(true))
  } catch (err: any) {
    toast.error('Falha ao verificar status da impressora remota!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getActiveFractionTagData() {
  yield put(appActions.isLoading(true))

  try {
    yield put(actions.clearField('fractionTag'))

    const result: ReturnCall<typeof apis.getActiveFractionTagData> = yield call(
      apis.getActiveFractionTagData,
    )

    if (!result.isOk) {
      toast.error(result.data?.data?.message || 'Falha ao buscar dados da etiqueta')
      return
    }

    yield put(actions.setFractionTag(result.data))
  } catch (err: any) {
    toast.error('Falha ao buscar dados da etiqueta!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* printFractionTag(data: PayloadAction<PrintFractionTagPayload>) {
  try {
    const fractionTag: FractionTag = yield select((state) => state.fraction.fractionTag)

    if (!fractionTag) {
      toast.error('Informações de etiqueta não encontradas')
      return
    }

    const printerServer: boolean = yield select((state) => state.fraction.printerServer)

    const result: ReturnCall<typeof apis.printFractionTag> = yield call(
      apis.printFractionTag,
      data.payload,
      fractionTag,
      printerServer,
    )

    if (!result.isOk) {
      toast.error(result.data?.data?.message || 'Falha ao imprimir etiqueta')
    }
  } catch (err: any) {
    toast.error('Falha ao imprimir etiqueta!')
  }
}

function* pauseFractioning(data: PayloadAction<PauseFractioningPayload>) {
  const {
    idBatch, badge, onSuccess, onError,
  } = data.payload

  try {
    const result: ReturnCall<typeof apis.pauseFractioning> = yield call(
      apis.pauseFractioning,
      idBatch,
      badge,
    )

    if (!result.isOk) {
      const errorsToIgnore = ['BATCH_NOT_IN_FRACTIONING', 'BATCH_WITHOUT_FRACTIONS']
      if (!errorsToIgnore.includes(result.data?.data?.errorCode)) {
        const message = result.data?.data?.message || 'Falha ao pausar o fracionamento'
        toast.error(message)
        onError(message)
        return
      }
    }

    onSuccess()
  } catch (err: any) {
    toast.error('Falha ao pausar o fracionamento!')
    onError('Falha ao pausar o fracionamento!')
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getScaleWeight.type, getScaleWeight),
    takeLatest(actions.checkLocalRaspberryStatus.type, checkLocalRaspberryStatus),
    takeLatest(actions.checkRemotePrinterStatus.type, checkRemotePrinterStatus),
    takeLatest(actions.getActiveFractionTagData.type, getActiveFractionTagData),
    takeLatest(actions.printFractionTag.type, printFractionTag),
    takeLatest(actions.pauseFractioning.type, pauseFractioning),
  ])
}
